import * as React from 'react';
import dateformat from 'dateformat';
import './PlaceListPage.css';
import { Link, useHistory } from 'react-router-dom';
import { Loading } from '../Loading';
import { useGetPlacesQuery, useUpdatePlacesCompletedMutation } from '../generated/graphql';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { AuthButton, Button } from '../components/Button';
import { PlaceTag } from '../components/PlaceTag';
import { Checkbox } from '../components/Input';
import { PlaceListBulkBar } from '../components/PlaceListBulkBar';
import { ToastContext } from '../components/Toast';
import { AppContext } from '../AppContext';
import {
  PlaceListView,
  togglePlaceSelection,
  areAllDisplayedSelected,
  toggleAllDisplayedSelection,
  isBulkBarVisible,
  toCompletedQueryVariable,
  toCompletedValueToWrite,
  getBulkActionLabel,
  getBulkActionSuccessMessage,
  getViewButtonColor,
  getInvertTagsButtonColor,
  getInvertTagsButtonLabel,
  getContainerClassName
} from './PlaceListPage.logic';

export const PlaceListPage: React.FC = function () {
  const [view, setView] = React.useState<PlaceListView>('inProgress');
  const [selectedPlaceIds, setSelectedPlaceIds] = React.useState<number[]>([]);
  const { loading, data, error, refetch } = useGetPlacesQuery({ variables: { completed: toCompletedQueryVariable(view) } });
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const appContext = React.useContext(AppContext);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [invertSelectedTags, setInvertSelectedTags] = React.useState<boolean>(false);

  const [updatePlacesCompleted, updatePlacesCompletedResult] = useUpdatePlacesCompletedMutation({
    onCompleted() {
      toastContext.showToast(getBulkActionSuccessMessage(view), 'success', 3000);
      setSelectedPlaceIds([]);
      refetch();
    },
    onError() {
      toastContext.showToast('更新に失敗しました', 'error', 5000);
    }
  });

  // 完了済マークを付け外しできるのは、新規作成と同じ権限を持つ人だけ
  const me = appContext.state.me;
  const canEdit = me !== undefined && me.authority.level >= 2;
  const bulkBarVisible = canEdit && isBulkBarVisible(selectedPlaceIds.length);

  if (error) {
    return <GraphQLErrorDialog error={error} />;
  }

  // 物件IDが選択されているタグかどうかを判定するマップを作成する
  // 何も選択されていない場合は undefined に設定する
  let selectedTagPlaceIds: { [key: number]: boolean } | undefined;
  if (selectedTags.length > 0 && data) {
    // 選択されているタグの名前からタグに紐付けられている物件IDのリストを取得し、物件IDを一つのリストにする
    const taggedPlaceIds = data.getPlaceTags
      .filter(placeTag => selectedTags.indexOf(placeTag.name) !== -1)
      .flatMap(placeTag => placeTag.place_ids);

    // タグに紐付いている物件がある場合にマップを作成する
    if (taggedPlaceIds.length > 0) {
      selectedTagPlaceIds = {};
      for (const taggedPlaceId of taggedPlaceIds) {
        selectedTagPlaceIds[taggedPlaceId] = true;
      }
    }
  }

  // タグ絞り込みと反転を適用した後、実際に表として表示する物件
  let displayedPlaces: NonNullable<typeof data>['getPlaces'];
  if (!data) {
    displayedPlaces = [];
  } else if (!selectedTagPlaceIds) {
    // タグが選択されていないならすべて表示する
    displayedPlaces = data.getPlaces;
  } else {
    displayedPlaces = data.getPlaces
      // タグが選択されている場合はタグに紐付いている物件のみを表示する
      .filter(place => {
        const isSelectedTagPlace = selectedTagPlaceIds![place.id];
        if (invertSelectedTags) {
          // 選択したタグ以外の物件を表示するモードの場合は判定を入れ替える
          return !isSelectedTagPlace;
        }
        return isSelectedTagPlace;
      });
  }
  const displayedPlaceIds = displayedPlaces.map(place => place.id);

  return (
    <div className={getContainerClassName(bulkBarVisible)}>
      <Loading open={loading || !data} />
        <h1>物件一覧</h1>
      <AuthButton authLv={2} onClick={() => history.push('/places/new')}>
        新規作成
      </AuthButton>
      <div className="PlaceListPage-tag_container">
        {data?.getPlaceTags.map(tag => {
          const selected = selectedTags.indexOf(tag.name) !== -1;
          return (
            <PlaceTag
              key={tag.name}
              selected={selected}
              onClick={() => {
                // 表示される物件が変わるので、見えなくなった物件が更新対象に残らないよう選択を空にする
                setSelectedPlaceIds([]);
                if (selected) {
                  setSelectedTags(selectedTags.filter(t => t !== tag.name));
                } else {
                  setSelectedTags(selectedTags.concat([tag.name]));
                }
              }}
              >
              {tag.name}
            </PlaceTag>
          );
        })}
      </div>
      <div className="PlaceListPage-tag_operations">
        <div className='PlaceListPage-tag_operation_button'>
          <Button color='white' mini onClick={() => {
            setSelectedPlaceIds([]);
            setSelectedTags([]);
          }}>タグの選択状態をクリア</Button>
        </div>
        <div className='PlaceListPage-tag_operation_button'>
          <Button color={getInvertTagsButtonColor(invertSelectedTags)} mini onClick={() => {
            setSelectedPlaceIds([]);
            setInvertSelectedTags(!invertSelectedTags);
          }}>
            {getInvertTagsButtonLabel(invertSelectedTags)}
          </Button>
        </div>
      </div>
      <div className="PlaceListPage-view_toggle">
        <Button
          color={getViewButtonColor(view, 'inProgress')}
          mini
          disabled={updatePlacesCompletedResult.loading}
          onClick={() => {
            setView('inProgress');
            setSelectedPlaceIds([]);
          }}
        >
          進行中
        </Button>
        <Button
          color={getViewButtonColor(view, 'completed')}
          mini
          disabled={updatePlacesCompletedResult.loading}
          onClick={() => {
            setView('completed');
            setSelectedPlaceIds([]);
          }}
        >
          完了済
        </Button>
      </div>
      <table className="App-table">
        <thead>
          <tr>
            {canEdit && (
              <th>
                <Checkbox
                  value={areAllDisplayedSelected(selectedPlaceIds, displayedPlaceIds)}
                  onChange={() => {
                    if (updatePlacesCompletedResult.loading) {
                      return;
                    }
                    setSelectedPlaceIds(toggleAllDisplayedSelection(selectedPlaceIds, displayedPlaceIds));
                  }}
                />
              </th>
            )}
            <th>現場名</th>
            <th>面数</th>
            <th>責任者</th>
            <th>お客様</th>
            <th>作成日</th>
            <th>更新日</th>
          </tr>
        </thead>
        <tbody>
          {displayedPlaces.map((place) => {
            if (!place) {
              return new Error();
            }
            return (
              <tr key={place.id}>
                {canEdit && (
                  <td>
                    <Checkbox
                      value={selectedPlaceIds.indexOf(place.id) !== -1}
                      onChange={() => {
                        if (updatePlacesCompletedResult.loading) {
                          return;
                        }
                        setSelectedPlaceIds(togglePlaceSelection(selectedPlaceIds, place.id));
                      }}
                    />
                  </td>
                )}
                <td>
                  <Link to={`/places/${place.id}`}>{place.name}</Link>
                </td>
                <td>{place.faces.length}</td>
                <td>{place.chief_name}</td>
                <td>{place.client_name}</td>
                <td>{dateformat(place.created, 'yyyy年m月d日 HH:MM:ss')}</td>
                <td>{dateformat(place.updated, 'yyyy年m月d日 HH:MM:ss')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {bulkBarVisible && (
        <PlaceListBulkBar
          selectedCount={selectedPlaceIds.length}
          actionLabel={getBulkActionLabel(view)}
          disabled={updatePlacesCompletedResult.loading}
          onAction={() => updatePlacesCompleted({ variables: { place_ids: selectedPlaceIds, completed: toCompletedValueToWrite(view) } })}
          onClearSelection={() => setSelectedPlaceIds([])}
        />
      )}
    </div>
  );
};
